import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Fab from "@mui/material/Fab";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import WineBarIcon from "@mui/icons-material/WineBar";
import { listTastings } from "../utils/apiClient";
import useDocumentTitle from "../hooks/useDocumentTitle";

function WineTastingJournal() {
  useDocumentTitle("Vinsmagning");
  const navigate = useNavigate();
  const [tastings, setTastings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await listTastings();
      setTastings(Array.isArray(data) ? data : []);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : err.message || "Kunne ikke indlæse smagninger");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Box sx={{ position: "relative", py: { xs: 1, sm: 2 } }}>
      <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: "-0.02em", mb: 1 }}>
        Vinsmagning
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Guidet smagning efter Court of Master Sommeliers’ grid. Gemmes som egen smagning — kan
        indsættes i et indlæg bagefter.
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : tastings.length === 0 ? (
        <Typography color="text.secondary">Ingen smagninger endnu.</Typography>
      ) : (
        <List>
          {tastings.map((t) => (
            <ListItemButton
              key={t.tasting_uuid}
              component={RouterLink}
              to={`/wine-tasting/${t.tasting_uuid}`}
            >
              <ListItemText
                primary={t.title}
                secondary={
                  t.created_at
                    ? new Date(t.created_at).toLocaleString()
                    : undefined
                }
              />
            </ListItemButton>
          ))}
        </List>
      )}

      <Fab
        color="primary"
        variant="extended"
        aria-label="Tilføj smagning, blind"
        onClick={() => navigate("/wine-tasting/new")}
        sx={{
          position: "fixed",
          right: { xs: 16, sm: 24 },
          bottom: { xs: 16, sm: 24 },
        }}
      >
        <WineBarIcon sx={{ mr: 1 }} />
        Tilføj smagning, blind
      </Fab>
    </Box>
  );
}

export default WineTastingJournal;
